import { useEffect, useState } from 'react';
import { Modal, Button } from '../../components/primitives';
import { AresSearchField, type AresPickResult } from '../../components/ares/AresSearchField';
import { CompanyPickerButton, type CompanyPickResult } from '../../components/ares/CompanyPickerButton';
import { SaveCompanyButton } from '../../components/ares/SaveCompanyButton';
import { DocContactQR } from '../../components/document/DocContactQR';
import { useDenikKontakty, useSaveDenikKontakty } from '../../api/hooks';
import type { DenikKontaktOsoba, DenikKontaktRole, DenikKontaktyMap } from '../../api/schemas';
import styles from './DenikKontakty.module.css';
import { cx } from '../../lib/cx';

/**
 * Modal „Kontakty pro deník" — Oddíl A staré verze.
 * Port z frontend/index.html denikRenderRoleBlocks + denikSaveKontakty
 * (řádky 20268 a 20526). Ukládá přes PUT /api/stavby/:id/denik-kontakty.
 */

interface RoleDef {
  key: string;
  label: string;
  hasCkait: boolean;
  /** Role v /api/companies/ — pro filter v CompanyPicker a role v SaveCompany. */
  companyRole: string;
}

const ROLES: RoleDef[] = [
  { key: 'investor', label: 'Investor / objednatel', hasCkait: false, companyRole: 'objednatel' },
  { key: 'zhotovitel', label: 'Zhotovitel', hasCkait: true, companyRole: 'zhotovitel' },
  { key: 'tds', label: 'Technický dozor stavebníka (TDS)', hasCkait: true, companyRole: 'tds' },
  { key: 'autorsky_dozor', label: 'Autorský dozor', hasCkait: true, companyRole: 'projektant' },
  { key: 'bozp_koordinator', label: 'Koordinátor BOZP', hasCkait: false, companyRole: 'bozp_koordinator' },
];

function ensureShape(raw: DenikKontaktyMap | undefined | null): DenikKontaktyMap {
  const out: DenikKontaktyMap = {};
  for (const r of ROLES) {
    const src = raw?.[r.key] ?? {};
    // Migrace starého plochého formátu (osoba/tel/email) na osoby[]
    let osoby: DenikKontaktOsoba[] = Array.isArray(src.osoby) ? src.osoby : [];
    if (osoby.length === 0 && ((src as { osoba?: string }).osoba || (src as { tel?: string }).tel || (src as { email?: string }).email)) {
      const legacy = src as { osoba?: string; tel?: string; email?: string; ckait?: string };
      osoby = [{
        jmeno: legacy.osoba ?? '',
        tel: legacy.tel ?? '',
        email: legacy.email ?? '',
        ckait: legacy.ckait ?? '',
        aktivni: true,
      }];
    }
    out[r.key] = {
      firma: src.firma ?? '',
      ico: src.ico ?? '',
      dic: src.dic ?? '',
      osoby,
    };
  }
  return out;
}

export interface DenikKontaktyModalProps {
  open: boolean;
  onClose: () => void;
  stavbaId: number;
}

export function DenikKontaktyModal({ open, onClose, stavbaId }: DenikKontaktyModalProps) {
  const { data: loaded, isLoading } = useDenikKontakty(stavbaId);
  const save = useSaveDenikKontakty(stavbaId);

  const [kontakty, setKontakty] = useState<DenikKontaktyMap>(() => ensureShape(undefined));
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setKontakty(ensureShape(loaded));
    setSavedMsg(null);
    setErr(null);
  }, [open, loaded]);

  function patchRole(key: string, patch: Partial<DenikKontaktRole>) {
    setKontakty((m) => ({ ...m, [key]: { ...(m[key] ?? {}), ...patch } }));
  }

  function patchOsoba(roleKey: string, i: number, patch: Partial<DenikKontaktOsoba>) {
    setKontakty((m) => {
      const role = m[roleKey] ?? {};
      const osoby = [...(role.osoby ?? [])];
      osoby[i] = { ...osoby[i], ...patch };
      return { ...m, [roleKey]: { ...role, osoby } };
    });
  }

  function addOsoba(roleKey: string) {
    setKontakty((m) => {
      const role = m[roleKey] ?? {};
      const osoby = [...(role.osoby ?? []), { jmeno: '', tel: '', email: '', aktivni: true }];
      return { ...m, [roleKey]: { ...role, osoby } };
    });
  }

  function delOsoba(roleKey: string, i: number) {
    setKontakty((m) => {
      const role = m[roleKey] ?? {};
      const osoby = (role.osoby ?? []).filter((_, j) => j !== i);
      return { ...m, [roleKey]: { ...role, osoby } };
    });
  }

  function deactivate(roleKey: string, i: number) {
    patchOsoba(roleKey, i, { aktivni: false, datum_do: new Date().toISOString().slice(0, 10) });
  }

  function reactivate(roleKey: string, i: number) {
    patchOsoba(roleKey, i, { aktivni: true, datum_do: '' });
  }

  async function handleSave() {
    setErr(null);
    setSavedMsg(null);
    try {
      await save.mutateAsync(kontakty);
      setSavedMsg('✓ Kontakty uloženy');
      setTimeout(() => setSavedMsg(null), 3000);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Nepodařilo se uložit.');
    }
  }

  function handleAresPick(roleKey: string, d: AresPickResult) {
    patchRole(roleKey, { firma: d.nazev, ico: d.ico, dic: d.dic });
  }

  function handleCompanyPick(roleKey: string, c: CompanyPickResult) {
    setKontakty((m) => {
      const role = m[roleKey] ?? {};
      const osoby = [...(role.osoby ?? [])];
      const activeIdx = osoby.findIndex((o) => o.aktivni !== false);
      const today = new Date().toISOString().slice(0, 10);
      const person: DenikKontaktOsoba = {
        jmeno: c.kontakt_jmeno || c.stavbyvedouci || '',
        tel: c.kontakt_tel,
        email: c.kontakt_email,
        ckait: c.ckait,
        podpis: '',
        datum_od: today,
        datum_do: '',
        aktivni: true,
      };
      if (activeIdx >= 0) osoby[activeIdx] = { ...osoby[activeIdx], ...person };
      else osoby.push(person);
      return {
        ...m,
        [roleKey]: {
          ...role,
          firma: c.nazev,
          ico: c.ico,
          dic: c.dic,
          osoby,
        },
      };
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      title="Kontakty — Oddíl A stavebního deníku"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Zavřít</Button>
          <Button variant="primary" onClick={handleSave} disabled={save.isPending}>
            {save.isPending ? 'Ukládám…' : 'Uložit kontakty'}
          </Button>
        </>
      }
    >
      <div className={styles.root}>
        {isLoading && <div style={{ color: 'var(--sd-text-dim)' }}>Načítám…</div>}
        {savedMsg && <div className={styles.statusOk}>{savedMsg}</div>}
        {err && <div className={styles.statusErr}>{err}</div>}

        {ROLES.map((role) => {
          const r = kontakty[role.key] ?? {};
          const osoby = r.osoby ?? [];
          const activePerson = osoby.find((o) => o.aktivni !== false) ?? {};
          return (
            <div key={role.key} className={styles.role}>
              <div className={styles.roleHead}>
                <div className={styles.roleTitle}>{role.label}</div>
                <CompanyPickerButton
                  preferRole={role.companyRole}
                  title={`Načíst ${role.label} z adresáře`}
                  onPick={(c) => handleCompanyPick(role.key, c)}
                />
                <SaveCompanyButton
                  title={`Uložit ${role.label} do adresáře`}
                  build={() => ({
                    role: role.companyRole,
                    nazev: r.firma ?? '',
                    ico: r.ico ?? '',
                    dic: r.dic ?? '',
                    kontakt_jmeno: activePerson.jmeno ?? '',
                    kontakt_tel: activePerson.tel ?? '',
                    kontakt_email: activePerson.email ?? '',
                    stavbyvedouci: role.hasCkait ? activePerson.jmeno ?? '' : '',
                    ckait: activePerson.ckait ?? '',
                    bozp_koordinator:
                      role.key === 'bozp_koordinator' ? activePerson.jmeno ?? '' : '',
                    bozp_tel: role.key === 'bozp_koordinator' ? activePerson.tel ?? '' : '',
                  })}
                />
              </div>

              <AresSearchField
                value={r.firma ?? ''}
                onChange={(v) => patchRole(role.key, { firma: v })}
                ico={r.ico ?? ''}
                onIcoChange={(v) => patchRole(role.key, { ico: v })}
                onPick={(d) => handleAresPick(role.key, d)}
                label="Firma"
                appearance="default"
              />

              <div className={styles.companyRow} style={{ marginTop: 8 }}>
                <div />
                <div className={styles.field}>
                  <label className={styles.label}>DIČ</label>
                  <input
                    className={styles.input}
                    value={r.dic ?? ''}
                    onChange={(e) => patchRole(role.key, { dic: e.target.value })}
                  />
                </div>
                <div />
              </div>

              {osoby.length === 0 && (
                <div style={{ fontSize: 12, color: 'var(--sd-text-dim)', padding: '8px 0' }}>
                  Zatím žádné kontaktní osoby.
                </div>
              )}

              {osoby.map((o, i) => {
                const inactive = !o.aktivni;
                return (
                  <div key={i} className={cx(styles.person, inactive && styles.inactive)}>
                    <div className={styles.personHead}>
                      <span className={cx(styles.personBadge, inactive && styles.ended)}>
                        {inactive ? 'UKONČEN' : 'AKTIVNÍ'}
                      </span>
                      <div style={{ marginLeft: 'auto' }}>
                        <DocContactQR
                          name={o.jmeno}
                          role={role.label}
                          org={r.firma}
                          phone={o.tel}
                          email={o.email}
                          compact
                          label="QR vizitka"
                          title={`QR vizitka — ${role.label}`}
                        />
                      </div>
                    </div>

                    <div className={styles.personGrid}>
                      <div className={styles.field}>
                        <label className={styles.label}>Jméno</label>
                        <input className={styles.input} value={o.jmeno ?? ''}
                          onChange={(e) => patchOsoba(role.key, i, { jmeno: e.target.value })} />
                      </div>
                      <div className={styles.field}>
                        <label className={styles.label}>Od</label>
                        <input className={styles.input} type="date" value={o.datum_od ?? ''}
                          onChange={(e) => patchOsoba(role.key, i, { datum_od: e.target.value })} />
                      </div>
                    </div>

                    <div className={styles.personGrid}>
                      <div className={styles.field}>
                        <label className={styles.label}>Telefon</label>
                        <input className={styles.input} value={o.tel ?? ''}
                          onChange={(e) => patchOsoba(role.key, i, { tel: e.target.value })} />
                      </div>
                      <div className={styles.field}>
                        <label className={styles.label}>Email</label>
                        <input className={styles.input} type="email" value={o.email ?? ''}
                          onChange={(e) => patchOsoba(role.key, i, { email: e.target.value })} />
                      </div>
                    </div>

                    {role.hasCkait && (
                      <div className={styles.personGrid}>
                        <div className={styles.field}>
                          <label className={styles.label}>ČKAIT</label>
                          <input className={styles.input} value={o.ckait ?? ''}
                            onChange={(e) => patchOsoba(role.key, i, { ckait: e.target.value })} />
                        </div>
                        <div className={styles.field}>
                          <label className={styles.label}>Jmenovka k podpisu</label>
                          <input className={styles.input} value={o.podpis ?? ''}
                            onChange={(e) => patchOsoba(role.key, i, { podpis: e.target.value })} />
                        </div>
                      </div>
                    )}

                    <div className={styles.personActions}>
                      {!inactive ? (
                        <button type="button" className={styles.smallBtn} onClick={() => deactivate(role.key, i)}>
                          ✖ Ukončit působení
                        </button>
                      ) : (
                        <button type="button" className={styles.smallBtn} onClick={() => reactivate(role.key, i)}>
                          ✓ Znovu aktivovat
                        </button>
                      )}
                      <button type="button" className={styles.smallBtn} onClick={() => delOsoba(role.key, i)}>
                        🗑 Smazat
                      </button>
                    </div>
                  </div>
                );
              })}

              <button type="button" className={styles.addPerson} onClick={() => addOsoba(role.key)}>
                + Přidat osobu
              </button>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}
